import React from 'react';
import FilterIcon from "../atoms/FilterIcon";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
import {connect} from "react-redux";
import SortIcon from "../atoms/SortIcon";
import GridFilter from "./GridFilter";
import {createSelector} from "reselect";
import InformationVariantIcon from "mdi-react/InformationVariantIcon";
import signals from "../../redux/actions/signals";
import {
    GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD,
    GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC,
    GRID_FILTER_TYPE_BOOLEAN,
} from "./GridFilter";

const getSorters = (state, {gridStateSliceName}) => state[gridStateSliceName].sorters;
const getFilters = (state, {gridStateSliceName}) => state[gridStateSliceName].filters;

const columnToTableName = new Map();
columnToTableName.set("courseName", "courses");
columnToTableName.set("providerName", "providers");
columnToTableName.set("regionName", "regions");
columnToTableName.set("deliveryTypeName", "deliveryTypes");
columnToTableName.set("cohortName", "cohorts");
columnToTableName.set("status", "statuses");
columnToTableName.set("fund", "funds");
columnToTableName.set("name", "courses");
columnToTableName.set("regionNames", "regions");
columnToTableName.set("deliveryTypeNames", "deliveryTypes");

// A row's value for a column can itself be an array (e.g. Courses' costTypeNames/regionNames/
// deliveryTypeNames, joined into a display string only by the column's own accessor) - treat each
// element as its own filterable option rather than the array as one opaque value, and represent an
// empty array as the same '' placeholder every other column uses for "nothing here".
const filterableValues = (row, columnName) => {
    const value = row[columnName];

    if (Array.isArray(value)) {
        return 0 === value.length ? [''] : value;
    }

    return [value];
};

const makeGetFilterOptions = columnName => createSelector(
    [
        (state, {gridStateSliceName}) => state[gridStateSliceName].data,
        getFilters,
    ],
    (data, filters) => [...new Set(data.flatMap(row => filterableValues(row, columnName)))].map(value => ({
        value,
        selected: Array.isArray(filters[columnName])
            ? filters[columnName].includes(value)
            : false,
    })),
);

const makeGetFilterOptionsStandardList = columnName => createSelector(
    [
        (state) => Object.values(state.data[columnToTableName.get(columnName)]),
        getFilters,
    ],
    (data, filters) => [...new Set(data.map(row => row["name"]))].map(value => ({
        value,
        selected: Array.isArray(filters[columnName])
            ? filters[columnName].includes(value)
            : false,
    })),
);

const makeGetFilterOptionsDynamicList = columnName => createSelector(
    [
      (state, { gridStateSliceName }) =>
        Object.values(state[gridStateSliceName].fuzzySearch?.[columnName] || []),
      getFilters,
    ],
    (data, filters) => {
      const valuesFromSearch = data.map(row => Object.values(row)[0]);

      const selectedValues = Array.isArray(filters[columnName]) ? filters[columnName] : [];

      const combinedValues = [...new Set([...valuesFromSearch, ...selectedValues])];

      return combinedValues.map(value => ({
        value,
        selected: selectedValues.includes(value),
      }));
    }
  );

const makeGetFilterOptionsBoolean = columnName => createSelector(
    [
        (state) => ['Yes', 'No'],
        getFilters,
    ],
    (data, filters) => [...new Set(data)].map(value => ({
        value,
        selected: Array.isArray(filters[columnName])
            ? filters[columnName].includes(value)
            : false,
    })),
);

const makeGetSortDirection = columnName => createSelector(
    [
        getSorters,
    ],
    (sorters) => (sorters.find(sorter => columnName === sorter.column) || {direction: null})['direction'],
);

const makeGetSortOrder = columnName => createSelector(
    [
        getSorters,
    ],
    (sorters) => sorters.map(({column}) => column).indexOf(columnName) + 1,
);

const mapStateToProps = (state, {column: {id: columnName, gridFilterType}}) => {
    // Memoized to improve performance (otherwise this component rerenders on every character input in modal)
    const getSortDirection = makeGetSortDirection(columnName);
    const getSortOrder = makeGetSortOrder(columnName);
    let getFilterOptions;
    // Memoized to improve performance (otherwise this component rerenders on every character input in modal)
    if (GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD === gridFilterType) {
        getFilterOptions = makeGetFilterOptionsStandardList(columnName);
    } else if (GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC === gridFilterType) {
        getFilterOptions = makeGetFilterOptionsDynamicList(columnName);
    } else if (GRID_FILTER_TYPE_BOOLEAN === gridFilterType) {
        getFilterOptions = makeGetFilterOptionsBoolean(columnName);
    } else {
        getFilterOptions = makeGetFilterOptions(columnName);
    }
    // Return a function in order to preserve memoized context of getters/selectors above
    return (state, {column, column: {id: columnName}, gridStateSliceName}) => ({
        column,
        // TODO improve this because it deals with text and date cases, it should only deal with one
        filterOptions: getFilterOptions(state, {gridStateSliceName}),
        filtersApplied: getFilters(state, {gridStateSliceName})[columnName],
        sortDirection: getSortDirection(state, {gridStateSliceName}),
        sortOrder: getSortOrder(state, {gridStateSliceName}),
    });
};

const mapDispatchToProps = (dispatch, {column: {id: columnName}, addValueToTextFilter, removeValueFromTextFilter, clearTextFilter, cycleColumnSortState, setDateFilter, clearDateFilter, reloadTable, backendSorted}) => ({
    addFilterValue: value => {
        dispatch(addValueToTextFilter(columnName, value))
        if (backendSorted) {
            dispatch(reloadTable(true));
        }
    },
    removeFilterValue: value => {
        dispatch(removeValueFromTextFilter(columnName, value))
        if (backendSorted) {
            dispatch(reloadTable(true));
        }
    },
    clearFilter: () => {
        dispatch(clearTextFilter(columnName))
        if (backendSorted) {
            dispatch(reloadTable(true));
        }
    },
    setDateFilter: range => {
        dispatch(setDateFilter(columnName, range))
        if (backendSorted) {
            dispatch(reloadTable(true));
        }
    },
    clearDateFilter: () => {
        dispatch(clearDateFilter(columnName))
        if (backendSorted) {
            dispatch(reloadTable(true));
        }
    },
    cycleSort: () => {
        dispatch(cycleColumnSortState(columnName))
        if (backendSorted) {
            dispatch(reloadTable());
        }
    },
});

const GridHeader = ({column, column: {id: columnName, disableFilters, gridFilterType}, filterOptions, addFilterValue, removeFilterValue, clearFilter, filtersApplied, sortDirection, sortOrder, cycleSort, setDateFilter, clearDateFilter}) => <React.Fragment>
    <span className="cell__sort">
        <SortIcon {...{sortDirection, sortOrder, cycleSort}}/>
    </span>
    <div className={`cell__text header-with-help ${true !== column.disableSortBy ? 'cell__sort' : ''}`}>
        <span onClick={() => true !== column.disableSortBy ? cycleSort() : null} className="">
            {column.render('Header')}
        </span>
        {
            column.tooltipMessage && <span className="help-icon">
                <Tippy
                    content={column.tooltipMessage}
                    placement="top"
                    animation="fade"
                    arrow theme="light-border"
                    appendTo={() => document.body}
                >
                    <span>
                        <InformationVariantIcon/>
                    </span>
                </Tippy>
            </span>
        }
    </div>
    {!disableFilters &&
        <Tippy
            content={<GridFilter {...{filterOptions, addFilterValue, removeFilterValue, clearFilter, filtersApplied, setDateFilter, clearDateFilter, type: gridFilterType, columnName: columnName}}/>}
            placement="bottom"
            animation="fade"
            arrow={false}
            theme="light-border"
            trigger="click"
            interactive={true}
            appendTo={() => document.body}
        >
            <span className="cell__filter">
                <FilterIcon {...{filtersApplied, columnName}}/>
            </span>
        </Tippy>
    }
</React.Fragment>;

export default connect(mapStateToProps, mapDispatchToProps)(GridHeader);
